import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useFetchData } from "../../../hooks/useFetch";
import {
  ApiConfig,
  API_ORDER_ROUTER,
  API_SERVICE_TYPE_MAPS_ROUTER,
  API_SERVICE_ADDON_ROUTER,
  API_CUSTOMER_PRICING_ROUTER,
} from "../../../services/api-config";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import { removeVietnameseFromString } from "../../../utils/helpers";
import { ITEM_TYPE } from "./BranchItemsPicker";

const fmt = (n: number) => new Intl.NumberFormat("vi-VN").format(Math.round(n || 0));

/** SL cho phép số lẻ (dòng kg); đơn giá chỉ số nguyên đồng. */
const sanitizeQty = (raw: string) => {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  return parts.length <= 1 ? cleaned : `${parts[0]}.${parts.slice(1).join("")}`;
};
const sanitizePrice = (raw: string) => raw.replace(/\D/g, "");

// Khoá định danh 1 dòng theo loại + id vật tư (dòng mới chưa có `order_items_v2.id` nên không dùng id được).
const keyOf = (itemType: number, itemId: number) => `${Number(itemType)}_${Number(itemId)}`;
const normalizeSearch = (v: string) => removeVietnameseFromString(v || "").toLowerCase();

interface LineInput {
  /** Khoá ổn định của dòng: `${item_type}_${item_id}`. */
  key: string;
  /** id dòng trong đơn (order_items_v2.id); dòng thêm mới = 0 cho tới khi BE lưu. */
  id: number;
  item_id: number;
  item_type: number;
  item_name: string;
  unit_name: string;
  isSurcharge: boolean;
  quantity: string;
  price: string;
  origQty: number;
  origPrice: number;
  /** Dòng vừa thêm trong modal, chưa tồn tại trên đơn. */
  isNew: boolean;
  /** Với dòng mới: giá lấy từ bảng giá riêng của KH (true) hay giá mặc định (false). */
  isCustomPrice?: boolean;
}

/** 1 lựa chọn trong autocomplete thêm vật tư. */
interface ItemOption {
  value: string;
  id: number;
  item_type: number;
  label: string;
  codes: string;
  unit: string;
  /** Giá mặc định (giá bảng của chi nhánh). */
  price: number;
  /** Giá riêng của khách cho vật tư này (nếu có). */
  customPrice: number | null;
}

interface Props {
  orderId: number;
  orderCode?: string;
  /** Khách của đơn — để tra giá riêng khi thêm vật tư. */
  customerId?: number;
  /** Chi nhánh chủ đơn — danh mục vật tư và giá riêng đều theo chi nhánh này. */
  branchId?: number;
  items: any[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Modal chỉnh số lượng thực tế + đơn giá + thêm / xoá dòng vật tư — chỉ cho người có quyền
 * `ORDER_ITEM_QUANTITY_EDIT` / `OPERATION_SUPERVISOR`. Đơn giá sửa ở đây CHỈ áp cho đơn này
 * (cột snapshot `order_details.price`), không đụng bảng giá dịch vụ / giá riêng của khách.
 * Mọi thay đổi ghi nhật ký số cũ → số mới kèm lý do.
 *
 * Lưu = MỘT lần gọi `POST :id/items` (sửa SL/giá `items` + xoá `remove_ids` + thêm `new_items`): BE áp tất
 * cả trong cùng 1 transaction, tự tra giá riêng / giá bảng cho dòng mới, chặn sửa tiền khi đã thu và điều
 * chỉnh sổ quỹ tài xế. (Trước 2026-10-22 tách 2 lần gọi `:id/update` + `:id/items` — lỗi giữa chừng để dữ
 * liệu nửa vời.) Xem `save` bên dưới.
 */
const OrderItemsEditModal: React.FC<Props> = ({
  orderId,
  orderCode,
  customerId,
  branchId,
  items,
  onClose,
  onSaved,
}) => {
  const { request: postUpdate } = useFetchData.Post<any>({
    projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
  });
  const { request: getMaps } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE });
  const { request: getAddons } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE });
  const { request: getPricing } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE });

  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<LineInput[]>(() =>
    items
      .filter((it) => Number(it.status) !== 8)
      .map((it) => ({
        key: keyOf(it.item_type, it.item_id),
        id: Number(it.id),
        item_id: Number(it.item_id),
        item_type: Number(it.item_type),
        item_name: it.item_name ?? `#${it.item_id}`,
        unit_name: it.unit_name || "",
        isSurcharge: Number(it.kind) === 2 || Number(it.item_type) === 2,
        quantity: String(Number(it.quantity) || 0),
        price: String(Math.round(Number(it.price) || 0)),
        origQty: Number(it.quantity) || 0,
        origPrice: Math.round(Number(it.price) || 0),
        isNew: false,
      }))
  );
  /** Dòng có sẵn trên đơn nhưng người dùng đã bấm xoá (chỉ thực sự xoá khi bấm Lưu; còn khôi phục được). */
  const [removed, setRemoved] = useState<LineInput[]>([]);

  // ===== Danh mục vật tư của chi nhánh + giá riêng của khách (cho autocomplete) =====
  const [options, setOptions] = useState<ItemOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    if (!branchId) return;
    let alive = true;
    setOptionsLoading(true);
    (async () => {
      try {
        const [mapRes, addonRes, priceRes] = await Promise.all([
          getMaps({
            endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_LIST,
            data: { status: 1, branch_id: branchId, store_id: branchId, limit: 1000 },
          }),
          getAddons({
            endPoint: API_SERVICE_ADDON_ROUTER.API_LIST,
            data: { status: 1, branch_id: branchId, store_id: branchId, limit: 1000 },
          }),
          // Không có khách (khách lẻ) → bỏ qua tra giá riêng, dùng giá mặc định.
          customerId
            ? getPricing({ endPoint: API_CUSTOMER_PRICING_ROUTER.API_BY_CUSTOMER_BRANCH(customerId, branchId) })
            : Promise.resolve(null),
        ]);
        if (!alive) return;

        // service_type_map_id → giá riêng. Giống BE (planItemsChange): chỉ nhận giá riêng đang active và > 0,
        // để giá hiển thị ở đây khớp đúng giá BE sẽ chốt khi lưu.
        const customMap = new Map<number, number>();
        if (priceRes && isSuccess(priceRes)) {
          const raw: any = (priceRes as any).data;
          const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
          for (const p of list) {
            const cp = Number(p.custom_price);
            if (Number(p.status) === 1 && !isNaN(cp) && cp > 0) customMap.set(Number(p.service_type_map_id), cp);
          }
        }

        const opts: ItemOption[] = [];
        if (mapRes && isSuccess(mapRes)) {
          for (const m of (mapRes as any)?.data?.list ?? []) {
            const svc = m.service?.name || m.service_name || "";
            const type = m.serviceType?.name || m.service_type?.name || m.service_type_name || "";
            const typeCode = m.serviceType?.code || m.service_type?.code || m.service_type_code || "";
            const svcCode = m.service?.code || m.service_code || "";
            opts.push({
              value: keyOf(ITEM_TYPE.SERVICE, Number(m.id)),
              id: Number(m.id),
              item_type: ITEM_TYPE.SERVICE,
              label: [svc, type].filter(Boolean).join(" - ") || `#${m.id}`,
              codes: [typeCode, svcCode].filter(Boolean).join(" · "),
              unit: m.unit?.name || m.unit_name || "",
              price: Number(m.price) || 0,
              customPrice: customMap.get(Number(m.id)) ?? null,
            });
          }
        }
        if (addonRes && isSuccess(addonRes)) {
          // Addon không có giá riêng theo khách → luôn giá mặc định.
          for (const a of (addonRes as any)?.data?.list ?? []) {
            opts.push({
              value: keyOf(ITEM_TYPE.ADDON, Number(a.id)),
              id: Number(a.id),
              item_type: ITEM_TYPE.ADDON,
              label: a.name || `#${a.id}`,
              codes: a.code || "",
              unit: "",
              price: Number(a.price) || 0,
              customPrice: null,
            });
          }
        }
        setOptions(opts);
      } catch {
        if (alive) showToast({ message: "Không tải được danh mục vật tư để thêm", type: "warning" });
      } finally {
        if (alive) setOptionsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line
  }, [branchId, customerId]);

  // Ẩn khỏi autocomplete những vật tư đã có trong danh sách (BE chặn chọn trùng).
  const addable = useMemo(() => {
    const picked = new Set(rows.map((r) => r.key));
    return options.filter((o) => !picked.has(o.value));
  }, [options, rows]);

  const setRow = (key: string, patch: Partial<LineInput>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  /** Chọn vật tư từ autocomplete: có giá riêng thì dùng giá riêng, không thì giá mặc định. */
  const addItem = (o: ItemOption | null) => {
    if (!o) return;
    const useCustom = o.customPrice !== null;
    const unitPrice = Math.round(useCustom ? (o.customPrice as number) : o.price);
    setRows((prev) =>
      prev.some((r) => r.key === o.value)
        ? prev
        : [
            ...prev,
            {
              key: o.value,
              id: 0,
              item_id: o.id,
              item_type: o.item_type,
              item_name: o.label,
              unit_name: o.unit,
              isSurcharge: o.item_type === ITEM_TYPE.ADDON,
              quantity: "1",
              price: String(unitPrice),
              origQty: 0,
              origPrice: unitPrice,
              isNew: true,
              isCustomPrice: useCustom,
            },
          ]
    );
    // Vừa xoá rồi chọn lại đúng vật tư đó → bỏ khỏi danh sách "đã xoá" để không bị tính 2 lần.
    setRemoved((prev) => prev.filter((r) => r.key !== o.value));
  };

  /** Xoá 1 dòng. Dòng mới thì bỏ luôn; dòng có sẵn thì nhớ lại để BE xoá khi lưu (và cho khôi phục). */
  const removeRow = (row: LineInput) => {
    setRows((prev) => prev.filter((r) => r.key !== row.key));
    if (!row.isNew) setRemoved((prev) => [...prev, row]);
  };

  const restoreRow = (row: LineInput) => {
    setRemoved((prev) => prev.filter((r) => r.key !== row.key));
    setRows((prev) => (prev.some((r) => r.key === row.key) ? prev : [...prev, row]));
  };

  const isQtyPriceChanged = (r: LineInput) =>
    Number(r.quantity) !== r.origQty || Number(r.price) !== r.origPrice;

  // Dòng có sửa SL / giá (dòng mới luôn tính là thay đổi vì nó là 1 thay đổi cấu trúc).
  const dirty = useMemo(
    () => rows.filter((r) => r.isNew || isQtyPriceChanged(r)),
    [rows]
  );
  const hasChange = dirty.length > 0 || removed.length > 0;
  const changeCount = dirty.length + removed.length;

  const newTotal = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.price) || 0), 0),
    [rows]
  );
  // Tổng cũ = tổng các dòng gốc, kể cả dòng đã bị xoá; dòng mới có origQty = 0 nên không cộng.
  const oldTotal = useMemo(
    () => [...rows, ...removed].reduce((s, r) => s + r.origQty * r.origPrice, 0),
    [rows, removed]
  );

  const save = async () => {
    if (!hasChange) {
      showToast({ message: "Chưa có thay đổi nào", type: "warning" });
      return;
    }
    if (!reason.trim()) {
      showToast({ message: "Vui lòng nhập lý do chỉnh sửa", type: "warning" });
      return;
    }
    // BE bắt buộc đơn còn ít nhất 1 dòng dịch vụ (chỉ addon thì không đủ).
    if (!rows.some((r) => r.item_type === ITEM_TYPE.SERVICE)) {
      showToast({ message: "Đơn phải có ít nhất một dịch vụ", type: "warning" });
      return;
    }
    for (const r of dirty) {
      if (!(Number(r.quantity) >= 0)) {
        showToast({ message: `Số lượng không hợp lệ: ${r.item_name}`, type: "warning" });
        return;
      }
      if (!(Number(r.price) >= 0)) {
        showToast({ message: `Đơn giá không hợp lệ: ${r.item_name}`, type: "warning" });
        return;
      }
    }

    setSaving(true);
    try {
      // MỘT lần gọi `POST :id/items` — BE áp sửa SL/giá + xoá + thêm dòng, tính lại tiền / công nợ / sản lượng
      // trong CÙNG transaction (lỗi giữa chừng → không lưu gì), chặn sửa tiền khi đã thu và điều chỉnh sổ quỹ.
      const res = await postUpdate({
        endPoint: API_ORDER_ROUTER.API_UPDATE_ITEMS(orderId),
        data: {
          reason: reason.trim(),
          // Dòng có sẵn: chỉ gửi dòng có đổi SL / giá.
          items: rows
            .filter((r) => !r.isNew && isQtyPriceChanged(r))
            .map((r) => ({ id: r.id, quantity: Number(r.quantity), price: Number(r.price) })),
          remove_ids: removed.map((r) => r.id).filter((id) => id > 0),
          // Dòng mới: chỉ gửi `price` khi người dùng sửa tay khác giá gợi ý — còn lại BE tự tra giá riêng / giá bảng.
          new_items: rows
            .filter((r) => r.isNew)
            .map((r) => ({
              item_id: r.item_id,
              item_type: r.item_type,
              quantity: Number(r.quantity) || 0,
              ...(Number(r.price) !== r.origPrice ? { price: Number(r.price) || 0 } : {}),
            })),
        },
      });
      if (!res || !isSuccess(res)) {
        showToast({ message: res?.message || "Lưu thất bại", type: "error" });
        return;
      }

      // Đơn tiền mặt đã hoàn tất: đơn đã lưu nhưng sổ quỹ tài xế chưa điều chỉnh được → phải báo kế toán.
      const cashLedger = (res as any)?.data?.cash_ledger;
      if (cashLedger && cashLedger.synced === false) {
        showToast({ message: cashLedger.message || "Đã lưu nhưng chưa điều chỉnh được sổ quỹ tài xế", type: "warning" });
      } else {
        showToast({ message: "Đã lưu chỉnh sửa vật tư", type: "success" });
      }
      onSaved();
    } catch (e: any) {
      showToast({ message: e?.message || "Lưu thất bại", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[780px] max-w-[95vw] max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-slate-800">Chỉnh vật tư / số lượng / đơn giá</h3>
          <p className="text-xs text-slate-500 mt-1">
            Đơn {orderCode ? <b>{orderCode}</b> : `#${orderId}`}. Đơn giá chỉnh ở đây chỉ áp cho đơn này,
            không đổi bảng giá. Mỗi thay đổi được ghi nhật ký kèm lý do.
          </p>
        </div>

        <div className="px-6 py-3 overflow-y-auto flex-1">
          {/* Autocomplete tìm theo tên / mã để thêm vật tư vào đơn */}
          <div className="mb-3">
            <Select
              placeholder={optionsLoading ? "Đang tải danh mục..." : "Tìm theo tên hoặc mã để thêm vật tư..."}
              options={addable}
              isLoading={optionsLoading}
              isDisabled={!branchId}
              value={null}
              onChange={(o: any) => addItem(o)}
              // Tìm theo tên HOẶC mã, không phân biệt hoa thường và dấu.
              filterOption={(option: any, input: string) => {
                const q = normalizeSearch(input.trim());
                if (!q) return true;
                return normalizeSearch(`${option.data.label} ${option.data.codes}`).includes(q);
              }}
              formatOptionLabel={(option: any) => (
                <div className="flex items-center justify-between gap-3">
                  <span>{option.label}</span>
                  <span className="shrink-0 text-xs text-slate-400">
                    {option.codes ? `${option.codes} · ` : ""}
                    {option.customPrice !== null ? (
                      <>
                        <b className="text-teal-600">{fmt(option.customPrice)}đ</b>
                        <span className="ml-1 text-teal-600">(giá riêng)</span>
                      </>
                    ) : (
                      `${fmt(option.price)}đ`
                    )}
                  </span>
                </div>
              )}
              isSearchable
              noOptionsMessage={() => "Không tìm thấy vật tư"}
              // Modal có overflow → thả menu ra body để không bị cắt; z cao hơn modal (z-50).
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              menuPosition="fixed"
              styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 60 }) }}
            />
          </div>

          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2 text-left">Dòng vật tư</th>
                <th className="py-2 text-right w-[110px]">SL thực tế</th>
                <th className="py-2 text-right w-[140px]">Đơn giá</th>
                <th className="py-2 text-right w-[120px]">Thành tiền</th>
                <th className="py-2 text-center w-[56px]">Xoá</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const changed = r.isNew || isQtyPriceChanged(r);
                return (
                  <tr key={r.key} className={r.isNew ? "bg-green-50" : changed ? "bg-amber-50" : ""}>
                    <td className="py-2 pr-2">
                      <div className="font-medium text-slate-800">
                        {r.item_name}
                        {r.isNew && <span className="ml-2 text-[11px] font-semibold text-green-600">Mới</span>}
                      </div>
                      <div className="text-xs text-slate-400">
                        {r.isSurcharge ? "Phụ phí" : "Dịch vụ"}
                        {r.unit_name ? ` · ${r.unit_name}` : ""}
                        {r.isNew && (
                          <span className={`ml-2 ${r.isCustomPrice ? "text-teal-600" : ""}`}>
                            · {r.isCustomPrice ? "Giá riêng của khách" : "Giá mặc định"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2">
                      <input
                        inputMode="decimal"
                        className="w-full border rounded-md px-2 py-1.5 text-right focus:ring-2 focus:ring-teal-300 focus:outline-none"
                        value={r.quantity}
                        onChange={(e) => setRow(r.key, { quantity: sanitizeQty(e.target.value) })}
                      />
                      {!r.isNew && Number(r.quantity) !== r.origQty && (
                        <div className="text-[11px] text-slate-400 text-right mt-0.5">từ {fmt(r.origQty)}</div>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <input
                        inputMode="numeric"
                        className="w-full border rounded-md px-2 py-1.5 text-right focus:ring-2 focus:ring-teal-300 focus:outline-none"
                        value={r.price}
                        onChange={(e) => setRow(r.key, { price: sanitizePrice(e.target.value) })}
                      />
                      {Number(r.price) !== r.origPrice && (
                        <div className="text-[11px] text-slate-400 text-right mt-0.5">
                          {r.isNew ? "gốc" : "từ"} {fmt(r.origPrice)}
                        </div>
                      )}
                    </td>
                    <td className="py-2 text-right font-semibold text-slate-800">
                      {fmt((Number(r.quantity) || 0) * (Number(r.price) || 0))}
                    </td>
                    <td className="py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeRow(r)}
                        disabled={saving}
                        title="Xoá vật tư khỏi đơn"
                        className="text-slate-400 hover:text-red-600 px-2 py-1 transition disabled:opacity-50"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-slate-400">
                    Đơn chưa có vật tư nào — hãy thêm ở ô tìm kiếm phía trên.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t">
                <td colSpan={3} className="py-2 text-right font-semibold text-slate-700">
                  Tổng tiền hàng {newTotal !== oldTotal && <span className="text-slate-400 font-normal">(cũ {fmt(oldTotal)})</span>}
                </td>
                <td className="py-2 text-right font-bold text-teal-700">{fmt(newTotal)}</td>
                <td />
              </tr>
            </tfoot>
          </table>

          {/* Dòng đã xoá — còn khôi phục được trước khi bấm Lưu */}
          {removed.length > 0 && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              <div className="text-xs font-medium text-red-700 mb-1">Sẽ xoá khỏi đơn khi lưu:</div>
              <div className="flex flex-wrap gap-2">
                {removed.map((r) => (
                  <span
                    key={r.key}
                    className="inline-flex items-center gap-1 rounded-full bg-white border border-red-200 px-2 py-0.5 text-xs text-slate-600"
                  >
                    <span className="line-through">{r.item_name}</span>
                    <button
                      type="button"
                      onClick={() => restoreRow(r)}
                      className="text-teal-600 hover:underline"
                    >
                      Khôi phục
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <label className="text-sm font-medium text-slate-700 block mb-1">
              Lý do chỉnh sửa <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-300 focus:outline-none resize-none"
              rows={2}
              placeholder="VD: NV kiểm đếm nhập nhầm 130 thành 13; chốt lại đơn giá theo thoả thuận với khách..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border rounded-lg text-slate-700 hover:bg-slate-100 transition"
          >
            Trở lại
          </button>
          <button
            onClick={save}
            disabled={saving || !hasChange || !reason.trim()}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition disabled:opacity-50"
          >
            {saving ? "Đang lưu..." : `Lưu chỉnh sửa${changeCount ? ` (${changeCount})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderItemsEditModal;
